import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useLocale } from "i18n/locale";

type ContactFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  setFormValues?: any;
  formValues: any;
  bookProperty: () => Promise<boolean>;
};


type FormErrors = {
  name?: string;
  email?: string;
  isChecked?: string;
};

const ContactFormModal: React.FC<ContactFormModalProps> = ({ isOpen, onClose, setFormValues, formValues, bookProperty }) => {
  const { t } = useLocale();

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Validation errors live in local state, so closing the modal (without a
  // successful submit) leaves them showing the next time it opens. Clear them
  // whenever the modal is closed so it always reopens in a clean state.
  useEffect(() => {
    if (!isOpen) {
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormValues({ ...formValues, [name]: type === "checkbox" ? checked : value });

    // Clear a field's validation message as soon as the user gives it a valid
    // value, instead of waiting for the next Submit. Only clears the error for
    // the field being edited, and only once its value actually passes.
    setErrors((prev) => {
      if (!prev[name as keyof FormErrors]) return prev;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      let nowValid = false;
      if (name === "name") nowValid = value.trim().length > 0;
      else if (name === "email") nowValid = emailRegex.test(value.trim());
      else if (name === "isChecked") nowValid = checked;
      if (!nowValid) return prev;
      const next = { ...prev };
      delete next[name as keyof FormErrors];
      return next;
    });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Submitting creates a real booking, so guard against a double click
    // producing two of them.
    if (submitting) return;

    const newErrors: FormErrors = {};

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formValues.name.trim()) {
      newErrors.name = t("reservationForm.nameRequired");
    }
    if (!formValues.email.trim()) {
      newErrors.email = t("reservationForm.emailRequired");
    } else if (!emailRegex.test(formValues.email.trim())) {
      newErrors.email = t("reservationForm.emailInvalid");
    }
    if (!formValues.isChecked) {
      newErrors.isChecked = t("reservationForm.termsRequired");
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSubmitError(null);
    setSubmitting(true);
    try {
      // Creates the booking and hands off to Stripe Checkout. Close only on
      // success — otherwise the guest loses the error along with the form.
      const booked = await bookProperty();
      if (booked) {
        onClose();
      } else {
        setSubmitError(t("reservationForm.bookingFailed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={onClose}>
        <div className="min-h-screen text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-40 dark:bg-opacity-60" />
          </Transition.Child>

          <span className="inline-block h-screen align-middle" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            className="inline-block py-8 px-2 h-screen w-full max-w-lg"
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div
              className="inline-flex flex-col w-full text-left align-middle transition-all transform overflow-hidden bg-white shadow-xl rounded-2xl"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              {/* Header — close (left, gold) + centered title, divider below */}
              {/* <div className="relative flex items-center px-8 py-5 border-b border-[#EADFCE]"> */}
                <div className="relative flex items-center px-8 py-7 border-b border-[#EADFCE]">
                <button
                  type="button"
                  className="absolute left-8 focus:outline-none hover:opacity-70"
                  onClick={onClose}
                  aria-label={t("filters.close")}
                >
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                    <path d="M1 1L15 15M15 1L1 15" stroke="#B88333" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </button>
                <Dialog.Title as="h3" className="w-full text-center text-[18px] font-semibold text-[#01152E]">
                  {t("reservationForm.title")}
                </Dialog.Title>
              </div>

              <form onSubmit={handleSubmit} className="px-8 py-5 space-y-4">
                {/* Full name */}
                <div>
                  <label htmlFor="name" className="block text-[14px] font-semibold text-[#01152E] mb-1.5">
                    {t("reservationForm.fullName")}
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#98A2B3]">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={formValues.name}
                      onChange={handleChange}
                      placeholder={t("reservationForm.fullNamePlaceholder")}
                      className={`block w-full pl-11 pr-3.5 py-3 text-[14px] text-[#01152E] placeholder:text-[#98A2B3] !rounded-xl border ${errors.name ? "!border-[#e07a7a]" : "!border-[#d6d6d6]"} focus:!border-[#01152E] focus:!ring-0 focus:outline-none`}
                    />
                  </div>
                  {errors.name && <p className="text-[#e05a5a] text-[12px] mt-1">{errors.name}</p>}
                </div>

                {/* Email address */}
                <div>
                  <label htmlFor="email" className="block text-[14px] font-semibold text-[#01152E] mb-1.5">
                    {t("reservationForm.emailAddress")}
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#98A2B3]">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="5" width="18" height="14" rx="2" />
                        <path d="m3 7 9 6 9-6" />
                      </svg>
                    </span>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={formValues.email}
                      onChange={handleChange}
                      placeholder={t("reservationForm.emailPlaceholder")}
                      className={`block w-full pl-11 pr-3.5 py-3 text-[14px] text-[#01152E] placeholder:text-[#98A2B3] !rounded-xl border ${errors.email ? "!border-[#e07a7a]" : "!border-[#d6d6d6]"} focus:!border-[#01152E] focus:!ring-0 focus:outline-none`}
                    />
                  </div>
                  {errors.email && <p className="text-[#e05a5a] text-[12px] mt-1">{errors.email}</p>}
                </div>

                {/* Phone number */}
                <div>
                  <label htmlFor="phone" className="block text-[14px] font-semibold text-[#01152E] mb-1.5">
                    {t("reservationForm.phoneNumber")}
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#98A2B3]">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 4h3l2 5-2.5 1.5a11 11 0 0 0 5 5L16 13l5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      name="phone"
                      id="phone"
                      value={formValues.phone}
                      onChange={handleChange}
                      placeholder={t("reservationForm.phonePlaceholder")}
                      className="block w-full pl-11 pr-3.5 py-3 text-[14px] text-[#01152E] placeholder:text-[#98A2B3] !rounded-xl border !border-[#d6d6d6] focus:!border-[#01152E] focus:!ring-0 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Terms checkbox */}
                <div>
                  <div className="flex items-start gap-2.5">
                    <span className="relative mt-0.5 flex-shrink-0 w-[18px] h-[18px]">
                      <input
                        type="checkbox"
                        id="isChecked"
                        name="isChecked"
                        checked={formValues.isChecked || false}
                        onChange={handleChange}
                        className="peer appearance-none w-[18px] h-[18px] cursor-pointer rounded-[5px] border border-[#d6d6d6] bg-white checked:bg-[#01152E] checked:border-[#01152E] focus:outline-none"
                      />
                      <svg
                        className="pointer-events-none absolute inset-0 m-auto hidden peer-checked:block"
                        width="11"
                        height="11"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path d="M2 6.2l2.6 2.6L10 3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <label htmlFor="isChecked" className="text-[13px] leading-[20px] text-[#445162]">
                      {t("reservationForm.termsPrefix")}{" "}
                      <a target="_blank" href="/travel-terms-of-use" className="text-[#B88333] font-medium underline underline-offset-2">
                        {t("reservationForm.termsLink1")}
                      </a>{" "}
                      {t("reservationForm.termsAnd")}{" "}
                      <a target="_blank" href="/terms-of-use" className="text-[#B88333] font-medium underline underline-offset-2">
                        {t("reservationForm.termsLink2")}
                      </a>
                      .
                    </label>
                  </div>
                  {errors.isChecked && <p className="text-[#e05a5a] text-[12px] mt-1">{errors.isChecked}</p>}
                </div>

                {submitError && (
                  <p className="text-[#e05a5a] text-[12px]">{submitError}</p>
                )}

                {/* Divider above the footer buttons — full-width to the card edges */}
                <div className="-mx-8 border-t border-[#EADFCE]" />

                {/* Footer — Cancel (outlined pill) + Submit (gold pill) */}
                <div className="flex justify-end items-center gap-4 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-8 py-2.5 rounded-full border border-[#B88333] bg-white text-[#01152E] text-[14px] font-semibold hover:bg-[#B88333]/10"
                  >
                    {t("reservationForm.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-8 py-2.5 rounded-full bg-[#B88333] text-white text-[14px] font-semibold hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? t("reservationForm.submitting") : t("reservationForm.submit")}
                  </button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ContactFormModal;
